import type { CSSProperties } from 'react';

import { memo } from 'react';
import { Icon as UinixIcon } from 'uinix-ui';

import { SharedBoxProps } from '../../types/SharedBoxProps';
import { SharedIconProps } from '../../types/SharedIconProps';
import { Tooltip } from '../Tooltip/Tooltip';

interface Props extends SharedIconProps, SharedBoxProps {
  /** Merged onto default tooltip Popover container styles when `tooltip` is set */
  tooltipContainerStyle?: CSSProperties;
  /** Merged onto default tooltip trigger styles when `tooltip` is set */
  tooltipTriggerStyle?: CSSProperties;
}

export const Icon = memo(
  ({
    color,
    label,
    size = 'm',
    tooltip,
    tooltipContainerStyle,
    tooltipTriggerStyle,
    ...rest
  }: Props) => {
    // label takes priority as a descriptive label for an icon if it is explicitly provided
    // label is SR friendly plain string, tooltip is HTML markup friendly
    const ariaLabel = label || tooltip;

    return (
      <Tooltip
        containerStyle={tooltip ? tooltipContainerStyle : undefined}
        tooltip={tooltip}
        triggerStyle={{ ...defaultTooltipTriggerStyle, ...tooltipTriggerStyle }}
      >
        <UinixIcon
          {...rest}
          aria-label={ariaLabel}
          // Decorative icons carry labels for tests/tooltips but must not leak into
          // parent link/control accessible names; tabIndex is on this node for tooltip
          // discovery only.
          // eslint-disable-next-line jsx-a11y/no-aria-hidden-on-focusable -- intentional
          aria-hidden="true"
          color={color}
          size={`icon.${size}`}
          styles={componentStyles.svg}
          // an `Icon` is never interactive, except when tooltips are specified (for tab-focus discovery of the tooltip)
          tabIndex={tooltip ? 0 : undefined}
        />
      </Tooltip>
    );
  },
);

const defaultTooltipTriggerStyle: CSSProperties = {
  display: 'flex',
};

const componentStyles = {
  svg: {
    '> svg': {
      pointerEvents: 'none',
    },
  },
};
